import React, { useLayoutEffect, useRef, useState } from 'react';

export interface TabControlProps {
  tabs: Array<{ label: string }>;
  activeIndex: number;
  onTabClick?: (index: number) => void;
}

export default function TabControl({ tabs, activeIndex, onTabClick }: TabControlProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const labelRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [underline, setUnderline] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const rootEl = rootRef.current;
      const labelEl = labelRefs.current[activeIndex];
      if (!rootEl || !labelEl) return;

      const rootRect = rootEl.getBoundingClientRect();
      const labelRect = labelEl.getBoundingClientRect();
      const buttonEl = labelEl.closest('button');
      const buttonRect = buttonEl?.getBoundingClientRect();

      const extraPx = 8;
      const buttonLeft = buttonRect ? buttonRect.left - rootRect.left : labelRect.left - rootRect.left;
      const buttonRight = buttonRect ? buttonRect.right - rootRect.left : labelRect.right - rootRect.left;

      const startLeft =
        activeIndex === 0
          ? Math.max(0, buttonLeft)
          : Math.max(0, (labelRect.left - rootRect.left) - extraPx);

      const endRight =
        activeIndex === 0
          ? Math.min(buttonRight, (labelRect.right - rootRect.left) + extraPx)
          : Math.min(buttonRight, (labelRect.right - rootRect.left) + extraPx);

      setUnderline({
        left: startLeft,
        width: Math.max(0, endRight - startLeft),
      });
    };

    measure();

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeIndex, tabs]);

  return (
    <div ref={rootRef} className="relative w-full">
      <div className="flex items-start gap-[0px]">
        {tabs.map((t, idx) => (
          <button
            key={`${t.label}-${idx}`}
            type="button"
            className="w-[266px] text-center font-['Hanken_Grotesk',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0.064px]"
            style={{
              fontVariationSettings: "'wdth' 100",
              color: activeIndex === idx ? '#132543' : '#A4B9CA',
            }}
            onClick={() => onTabClick?.(idx)}
          >
            <span ref={(el) => {
              labelRefs.current[idx] = el;
            }} className="inline-block whitespace-nowrap">
              {t.label}
            </span>
          </button>
        ))}
      </div>

      <div className="relative mt-[11px] h-[4px] w-full">
        <div className="absolute left-0 top-[1px] h-[2px] w-full bg-[#A4B9CA]" />

        <div
          className="absolute top-0 h-[4px] bg-[#53BEE8]"
          style={{ left: underline.left, width: underline.width }}
        />
      </div>
    </div>
  );
}
